/* eslint-disable */
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Grid,
  Typography,
} from '@material-ui/core';
import { getSrc } from 'gatsby-plugin-image';
import { graphql, Link } from 'gatsby';
import * as React from 'react';
import isEqual from 'lodash.isequal';

import { Layout } from '../../../components/layout';
import { NumericInput } from '../../../components/numeric-input';
import { StoreContext } from '../../../context/store-context';
import { formatPrice } from '../../../utils/format-price';
import AddToCart from '../../../components/add-to-cart';
import OptionButton from '../../../components/styled/option-button';
import ProductGallery from '../../../components/product-gallery';
import ProductListing from '../../../components/product-listing';
import Seo from '../../../components/seo';
import useProductStyles from '../../../styles/product';

export default function Product({ data: { product, suggestions } }) {
  const classes = useProductStyles();
  const {
    options,
    variants,
    variants: [initialVariant],
    priceRangeV2,
    title,
    description,
    images,
    images: [firstImage],
  } = product;
  const { client } = React.useContext(StoreContext);

  const [variant, setVariant] = React.useState({ ...initialVariant });
  const [quantity, setQuantity] = React.useState(1);

  const productVariant =
    client.product.helpers.variantForOptions(product, variant) || variant;

  const [available, setAvailable] = React.useState(
    productVariant.availableForSale,
  );

  const checkAvailablity = React.useCallback(
    (productId) => {
      client.product.fetch(productId).then((fetchedProduct) => {
        const result =
          fetchedProduct?.variants.filter(
            (variant) => variant.id === productVariant.storefrontId,
          ) ?? [];

        if (result.length > 0) {
          setAvailable(result[0].available);
        }
      });
    },
    [productVariant.storefrontId, client.product],
  );

  const handleOptionChange = (index, value) => {
    if (value === '') {
      return;
    }

    const currentOptions = [...variant.selectedOptions];

    currentOptions[index] = {
      ...currentOptions[index],
      value,
    };

    const selectedVariant = variants.find((variant) => {
      return isEqual(currentOptions, variant.selectedOptions);
    });

    setVariant({ ...selectedVariant });
  };

  React.useEffect(() => {
    checkAvailablity(product.storefrontId);
  }, [productVariant.storefrontId, checkAvailablity, product.storefrontId]);

  const price = formatPrice(
    priceRangeV2.minVariantPrice.currencyCode,
    variant.price,
  );

  const minPrice = formatPrice(
    priceRangeV2.minVariantPrice.currencyCode,
    variant.compareAtPrice || variant.price,
  );

  const hasVariants = variants.length > 1;
  const hasImages = images.length > 0;
  const hasMultipleImages = true || images.length > 1;

  return (
    <Layout>
      {firstImage ? (
        <Seo
          title={title}
          description={description}
          image={getSrc(firstImage.gatsbyImageData)}
        />
      ) : undefined}

      <Grid container>
        <ProductGallery images={images} title={title} />

        <Grid
          item
          container
          lg={4}
          xs={12}
          md={5}
          className={classes.productDescriptionContainer}
          justifyContent="center"
        >
          <Grid item xs={10}>
            {!available && (
              <Chip
                className={classes.chip}
                color="secondary"
                label="Agotado"
              />
            )}
            &nbsp;
            {minPrice !== price && <Chip color="secondary" label="Descuento" />}
            <Typography variant="subtitle1">
              Productos oficiales {product.vendor}&trade;
            </Typography>
            <Typography variant="h4">{product.title}</Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link color="inherit" to="/search">
                Productos
              </Link>
              <Link color="inherit" to={product.productTypeSlug}>
                {product.productType}
              </Link>
            </Breadcrumbs>
            <br />
            {minPrice !== price ? (
              <Typography paragraph variant="h6">
                De <span className={classes.price}>{minPrice}</span>
                &nbsp;a&nbsp;
                {price}
              </Typography>
            ) : (
              <Typography paragraph variant="h6">
                Su precio: {price}
              </Typography>
            )}
            <br />
            <Box p={2} className={classes.variantsContainer}>
              {product.options.map(({ name, id, values }, index) => (
                <Box paddingBottom={2} key={id}>
                  <Typography variant="subtitle1">{name}</Typography>
                  {values.map((value) => (
                    <OptionButton
                      selected={isEqual(variant.selectedOptions[index], {
                        name,
                        value,
                      })}
                      onClick={() => handleOptionChange(index, value)}
                      key={`${name}-${value}`}
                    >
                      {value}
                    </OptionButton>
                  ))}
                </Box>
              ))}

              <Box>
                <Typography variant="subtitle1">Cantidad</Typography>
                <NumericInput
                  aria-label="Quantity"
                  onIncrement={() => setQuantity((q) => Math.min(q + 1, 20))}
                  onDecrement={() => setQuantity((q) => Math.max(1, q - 1))}
                  onChange={(event) => setQuantity(event.currentTarget.value)}
                  value={quantity}
                  min="1"
                  max="20"
                />

                <AddToCart
                  variantId={productVariant.storefrontId}
                  quantity={quantity}
                  available={available}
                />
              </Box>
            </Box>
            <div>{product.description}</div>
          </Grid>
        </Grid>
      </Grid>
      {suggestions?.nodes.length && (
        <ProductListing
          title="También puedes comprar"
          products={suggestions.nodes}
        />
      )}
    </Layout>
  );
}

export const query = graphql`
  query ($id: String!, $productType: String!) {
    product: shopifyProduct(id: { eq: $id }) {
      title
      description
      descriptionHtml
      productType
      productTypeSlug: gatsbyPath(
        filePath: "/products/{ShopifyProduct.productType}"
      )
      vendor
      tags
      priceRangeV2 {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      storefrontId
      images {
        altText
        id
        gatsbyImageData(layout: CONSTRAINED, width: 640, aspectRatio: 1)
        thumbnail: gatsbyImageData(
          layout: CONSTRAINED
          width: 100
          aspectRatio: 1
        )
      }
      variants {
        availableForSale
        storefrontId
        title
        price
        compareAtPrice
        selectedOptions {
          name
          value
        }
      }
      options {
        name
        values
        id
      }
    }
    suggestions: allShopifyProduct(
      limit: 8
      filter: { productType: { eq: $productType }, id: { ne: $id } }
    ) {
      nodes {
        ...ProductCard
      }
    }
  }
`;
